import { ERROR_TYPES } from '../../components/Modal/ErrorModal';

/**
 * 백엔드 에러 메시지를 분석하여 적절한 에러 타입을 반환합니다.
 */
export const parseErrorType = (error) => {
  // 에러가 없거나 문자열이 아닌 경우
  if (!error) return ERROR_TYPES.UNKNOWN_ERROR;
  
  // Error 객체인 경우 메시지 추출
  const errorMessage = typeof error === 'string' ? error : error.message || error.detail || String(error);
  const lowerMessage = errorMessage.toLowerCase();

  // 자막 관련 에러 - 구체적인 "자막 없음" 먼저 체크
  if (lowerMessage.includes('자막이 없습니다') || 
      lowerMessage.includes('no transcript') ||
      lowerMessage.includes('자막을 찾을 수 없습니다') ||
      lowerMessage.includes('이 영상에는 자막이 없습니다') ||
      lowerMessage.includes('사용 가능한 자막이 없어서') ||
      lowerMessage.includes('자막이 없어서 서비스를 제공할 수 없습니다') ||
      lowerMessage.includes('이 영상에는 사용 가능한 자막이 없습니다')) {
    return ERROR_TYPES.NO_CAPTIONS;
  }

  // 지원하지 않는 언어 또는 자막 처리 실패
  if (lowerMessage.includes('자막을 처리할 수 없습니다') ||
      lowerMessage.includes('자막 형식이나 접근 권한에 문제') ||
      lowerMessage.includes('지원하는 자막을 찾을 수 없습니다') ||
      lowerMessage.includes('지원: 한국어/영어 자막') ||
      lowerMessage.includes('unsupported language')) {
    return ERROR_TYPES.UNSUPPORTED_LANGUAGE;
  }

  // 자막 추출 실패 - 일반적인 경우 (구체적인 원인을 찾지 못한 경우)
  if (lowerMessage.includes('자막 추출 실패') ||
      lowerMessage.includes('자막 추출에 실패했습니다') ||
      lowerMessage.includes('자막을 가져오는데 실패') ||
      lowerMessage.includes('자막 데이터 파싱에 실패') ||
      lowerMessage.includes('잠시 후 다시 시도해 주세요')) {
    return ERROR_TYPES.EXTRACTION_FAILED;
  }

  // 🔥 YouTube 타임아웃 관련 에러 (15초 초과)
  if (lowerMessage.includes('youtube 자막 추출이 15초를 초과') ||
      lowerMessage.includes('youtube 측의 일시적인 제한') ||
      lowerMessage.includes('youtube에서 임시적으로 접근을 제한') ||
      lowerMessage.includes('youtube caption extraction delayed') ||
      lowerMessage.includes('youtube is temporarily limiting access')) {
    return ERROR_TYPES.YOUTUBE_TIMEOUT;
  }

  // URL 관련 에러
  if (lowerMessage.includes('올바른 유튜브 url이 아닙니다') ||
      lowerMessage.includes('올바른 youtube url') ||
      lowerMessage.includes('invalid url') ||
      lowerMessage.includes('유효한 youtube url')) {
    return ERROR_TYPES.INVALID_URL;
  }

  // 비디오 정보 없음
  if (lowerMessage.includes('영상 정보를 가져올 수 없습니다') ||
      lowerMessage.includes('영상을 찾을 수 없습니다') ||
      lowerMessage.includes('video not found') ||
      lowerMessage.includes('삭제된 영상') ||
      lowerMessage.includes('비공개 영상')) {
    return ERROR_TYPES.VIDEO_NOT_FOUND;
  }

  // 네트워크 에러
  if (lowerMessage.includes('network') ||
      lowerMessage.includes('네트워크') ||
      lowerMessage.includes('connection') ||
      lowerMessage.includes('연결') ||
      lowerMessage.includes('timeout') ||
      lowerMessage.includes('시간 초과')) {
    return ERROR_TYPES.NETWORK_ERROR;
  }

  // 서버 에러
  if (lowerMessage.includes('서버') ||
      lowerMessage.includes('server') ||
      lowerMessage.includes('internal') ||
      lowerMessage.includes('503') ||
      lowerMessage.includes('502') ||
      lowerMessage.includes('500')) {
    return ERROR_TYPES.SERVER_ERROR;
  }

  // 라이브 방송/실시간 방송
  if (lowerMessage.includes('live') ||
      lowerMessage.includes('라이브') ||
      lowerMessage.includes('실시간') ||
      lowerMessage.includes('방송 중') ||
      lowerMessage.includes('streaming') ||
      lowerMessage.includes('premiere') ||
      lowerMessage.includes('현재 실시간 방송') ||
      lowerMessage.includes('예정된 라이브 방송') ||
      lowerMessage.includes('실시간 방송은 요약할 수 없습니다') ||
      lowerMessage.includes('방송이 종료된 후') ||
      lowerMessage.includes('live broadcast')) {
    return ERROR_TYPES.LIVE_STREAM;
  }

  // YouTube 쇼츠
  if (lowerMessage.includes('shorts') ||
      lowerMessage.includes('쇼츠') ||
      lowerMessage.includes('short') ||
      lowerMessage.includes('짧은 영상') ||
      lowerMessage.includes('세로형 영상')) {
    return ERROR_TYPES.YOUTUBE_SHORTS;
  }

  // 중복 비디오는 다른 방식으로 처리되므로 여기서는 체크하지 않음

  // 기본값
  return ERROR_TYPES.UNKNOWN_ERROR;
};

/**
 * HTTP 상태 코드를 기반으로 에러 타입을 반환합니다.
 */
export const parseErrorTypeFromStatus = (status, message = '') => {
  switch (status) {
    case 400:
      return parseErrorType(message);
    case 404:
      return ERROR_TYPES.VIDEO_NOT_FOUND;
    case 500:
    case 502:
    case 503:
      return ERROR_TYPES.SERVER_ERROR;
    case 408:
    case 504:
      return ERROR_TYPES.NETWORK_ERROR;
    default:
      return parseErrorType(message);
  }
};

/**
 * fetch API 에러를 분석하여 에러 타입을 반환합니다.
 */
export const parseErrorFromResponse = async (response) => {
  if (!response) return ERROR_TYPES.NETWORK_ERROR;
  
  try {
    const errorData = await response.json();
    const message = errorData.detail || errorData.message || '';
    const details = errorData.details || '';
    
    // details와 message를 합쳐서 분석 (더 정확한 에러 감지)
    const combinedMessage = `${message} ${details}`.trim();
    
    return parseErrorTypeFromStatus(response.status, combinedMessage);
  } catch {
    // JSON 파싱 실패 시 상태 코드만으로 판단
    return parseErrorTypeFromStatus(response.status);
  }
};

/**
 * 중복 비디오 체크 (간소화된 버전)
 * active 상태의 비디오만을 중복으로 간주합니다.
 * 
 * @param {string} videoId - 체크할 비디오 ID
 * @param {Array} history - 히스토리 배열
 * @returns {Object|null} 중복된 히스토리 객체 또는 null
 */
export const checkDuplicateVideo = (videoId, history) => {
  return history.find(item => 
    item.videoId === videoId && 
    item.status === 'active'
  ) || null;
};

/**
 * 중복 비디오 체크 (언어별 체크 포함)
 * active 상태의 비디오에서만 videoId + language 조합으로 중복을 체크합니다.
 * analyzing, stopped, deleted, error 상태는 재분석이 가능하므로 중복으로 보지 않습니다.
 * 
 * @param {string} videoId - 체크할 비디오 ID
 * @param {string} language - 체크할 언어 코드 (예: 'ko', 'en', 'ja')
 * @param {Array} history - 히스토리 배열
 * @returns {Object|null} 중복된 히스토리 객체 또는 null
 */
export const checkDuplicateVideoWithLanguage = (videoId, language, history) => {
  return history.find(item => 
    item.videoId === videoId && 
    item.language === language &&
    item.status === 'active'
  ) || null;
};

/**
 * YouTube 영상이 쇼츠인지 확인하는 함수
 */
export const checkIsYouTubeShorts = (url = '') => {
  // URL에서 쇼츠 감지 - 가장 확실하고 간단한 방법
  return url.includes('/shorts/') || url.includes('youtube.com/shorts');
}; 